/**
 * CreditsAPI — notification credit wallet (WhatsApp / SMS / webpush sends are
 * metered against a per-clinic prepaid balance, in paise; email is free).
 *
 * Mirrors billing.service.ts's shape exactly: thin apiRequest wrappers, field
 * names kept snake_case to match the FastAPI JSON verbatim (not camelCased).
 */
import { apiRequest } from "./api.client";
import { ApiError } from "./api.errors";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface WalletBalance {
  balance_paise: number;
  updated_at: string;
}

/** One-time Razorpay order for a credit top-up (not a subscription). */
export interface TopupOrder {
  order_id: string;
  amount_paise: number;
  razorpay_key_id: string;
}

// ── API ────────────────────────────────────────────────────────────────────────

export const CreditsAPI = {
  getWallet: () => apiRequest<WalletBalance>("/credits/wallet"),

  /**
   * Create a one-time top-up order. `units` is the number of
   * CUSTOM_CREDITS_STEP increments above CUSTOM_CREDITS_MIN — the server
   * computes amount_paise itself: (CUSTOM_CREDITS_MIN + units * CUSTOM_CREDITS_STEP) * 100.
   * ROLE_ADMIN only on the backend.
   */
  createTopupOrder: (units: number) =>
    apiRequest<TopupOrder>("/credits/order", {
      method: "POST",
      body: JSON.stringify({ units }),
    }),
};

// ── 402 interception helper ─────────────────────────────────────────────────
// Same convention as the existing `feature_not_enabled` / `trial_expired`
// feature-gate 402s (see UpgradePromptModal usages) — metered send endpoints
// (prescription whatsapp, invoice whatsapp, sms/webpush) return this exact
// shape when the clinic's wallet can't cover the send (email sends are not
// metered and never trigger this):
//   { error: "insufficient_credits", clinic_id, upgrade_url }
export function isInsufficientCreditsError(e: unknown): boolean {
  return e instanceof ApiError && e.isPaymentRequired && e.message === "insufficient_credits";
}
